import {
  FormControl,
  FormControlTypeMap,
  InputAdornment,
  MenuItem,
  SelectProps,
  SxProps,
  Theme
} from "@mui/material";
import { COLORS } from "assets";
import React, { forwardRef, ReactNode } from "react";
import { LabelValueType } from "types";
import StyledHelperText from "./StyledHelperText";
import StyledInputLabel from "./StyledInputLabel";
import StyledSelect from "./StyledSelect";

interface ISelectInput extends SelectProps {
  data: LabelValueType<string | number>[];
  loading?: boolean;
  inputContainerProps?: React.HTMLAttributes<HTMLDivElement>;
  inputLabelProps?: React.InputHTMLAttributes<HTMLLabelElement>;
  formControlProps?: FormControlTypeMap;
  label?: string;
  helperText?: string;
  fullWidth?: boolean;
  error?: boolean;
  startAdornment?: ReactNode;
}

const fieldSelectSx: SxProps<Theme> = {
  height: "48px !important",
  minHeight: "48px !important",
  borderRadius: `${COLORS.radius.pill}px`,
  boxSizing: "border-box",
  "& .MuiSelect-select": {
    pl: "4px !important",
    pr: "32px !important",
    height: "48px !important",
    minHeight: "48px !important",
    lineHeight: "48px !important",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box"
  },
  "& .MuiInputAdornment-root": {
    height: "100%",
    maxHeight: "none"
  }
};

const defaultMenuProps: SelectProps["MenuProps"] = {
  disableScrollLock: true,
  disablePortal: false,
  style: { zIndex: 1500 },
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  marginThreshold: 8,
  PaperProps: {
    sx: {
      mt: 0,
      borderRadius: `${COLORS.radius.md}px`,
      border: `1px solid ${COLORS.surface.border}`,
      boxShadow: COLORS.shadow.card,
      maxHeight: 240,
      "& .MuiMenuItem-root": {
        color: COLORS.surface.primaryText,
        fontSize: 14,
        fontWeight: 500,
        "&.Mui-selected": {
          bgcolor: "rgba(30, 152, 217, 0.08)",
          color: COLORS.primary,
          "&:hover": { bgcolor: "rgba(30, 152, 217, 0.12)" }
        },
        "&:hover": { bgcolor: COLORS.surface.tableHeader }
      }
    }
  }
};

const SelectInput = forwardRef<any, ISelectInput>(
  (
    {
      inputContainerProps: _inputContainerProps,
      label,
      inputLabelProps,
      helperText = " ",
      formControlProps: _formControlProps,
      fullWidth,
      error,
      loading,
      data,
      startAdornment,
      MenuProps,
      sx,
      ...selectProps
    },
    ref
  ) => {
    const menuProps: SelectProps["MenuProps"] = {
      ...defaultMenuProps,
      ...MenuProps,
      PaperProps: {
        ...defaultMenuProps?.PaperProps,
        ...MenuProps?.PaperProps,
        sx: [
          (defaultMenuProps?.PaperProps as { sx?: SxProps<Theme> } | undefined)?.sx,
          (MenuProps?.PaperProps as { sx?: SxProps<Theme> } | undefined)?.sx
        ].filter(Boolean) as SxProps<Theme>
      }
    };

    const select = loading ? (
      <StyledSelect
        disabled
        fullWidth
        placeholder="Loading..."
        value="-1"
        startAdornment={
          startAdornment ? (
            <InputAdornment position="start" sx={{ ml: 2, mr: 0.5, color: COLORS.surface.secondaryText }}>
              {startAdornment}
            </InputAdornment>
          ) : undefined
        }
        MenuProps={menuProps}
        sx={startAdornment ? [fieldSelectSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])] : sx}
      >
        <MenuItem value="-1">Loading...</MenuItem>
      </StyledSelect>
    ) : (
      <StyledSelect
        {...selectProps}
        fullWidth={fullWidth}
        ref={ref}
        startAdornment={
          startAdornment ? (
            <InputAdornment position="start" sx={{ ml: 2, mr: 0.5, color: COLORS.surface.secondaryText }}>
              {startAdornment}
            </InputAdornment>
          ) : undefined
        }
        MenuProps={menuProps}
        sx={startAdornment ? [fieldSelectSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])] : sx}
      >
        {data.map(({ value, label: itemLabel }, i) => (
          <MenuItem key={i} value={value}>
            {itemLabel}
          </MenuItem>
        ))}
      </StyledSelect>
    );

    return (
      <FormControl fullWidth={fullWidth} sx={{ position: "relative", overflow: "visible" }}>
        {label && <StyledInputLabel {...inputLabelProps}>{label}</StyledInputLabel>}
        {select}
        {helperText && <StyledHelperText error={error}>{helperText}</StyledHelperText>}
      </FormControl>
    );
  }
);

export default SelectInput;
